import React, { useEffect, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { Btn, Select, TextInput } from '../kit';
import { TECHPAGE_EVENTS } from '../../../landing-page/components/techpages/track';
import { num, saveBlob, str } from '../ipintel/shared';

/**
 * Vocabulary and plumbing shared by the "Tech URL pages" admin tabs (and the
 * Universities tab's carousel / listing-request pieces).
 *
 * Everything reads through the panel dispatcher, section "tech_pages":
 *  - Backend/tech_pages.py: settings, settings_save, rebuild_paths, paths,
 *    path_override, preview_links (URL patterns, section matrix, SEO, limits);
 *  - Backend/admin_tech_pages.py: overview, pages, page_detail (traffic).
 * The Excel export is a raw admin route (EXPORT_PATH) because JSON cannot
 * carry a workbook.
 */

export const S = 'tech_pages';

/** GET ?kind=tech|university plus the Pages tab's filters → .xlsx (admin_tech_pages_routes). */
export const EXPORT_PATH = '/api/v1/admin/tech-pages/export.xlsx';
/** GET ?q&status&from&to → .xlsx of the listing requests (university_listing_routes). */
export const LISTING_EXPORT_PATH = '/api/v1/admin/universities/listing-requests.xlsx';

/** Where university listing requests are mailed (university_listing TO address). */
export const TEAM_INBOX = 'info@universitytech.io';

// ---------------------------------------------------------------------------
// Page states, sections, CTAs — ids exactly as tech_pages.py uses them.
// ---------------------------------------------------------------------------
export const STATES = ['pre_login', 'post_login', 'paid'] as const;
export type PageState = typeof STATES[number];
export const STATE_LABELS: Record<string, string> = {
  pre_login: 'Pre-login', post_login: 'Post-login', paid: 'Paid',
};
export const STATE_HINTS: Record<string, string> = {
  pre_login: 'Not signed in — also what crawlers and scrapers get',
  post_login: 'Signed in, no paid plan (Explorer, or a trial that has run out)',
  paid: 'Trial in progress, Voyager, or Voyager assigned',
};

export const SECTIONS = ['summary', 'trl', 'value_levers', 'researchers_ip', 'problem_applications',
  'university_details', 'industry_insights', 'similar_tech'] as const;
export const SECTION_LABELS: Record<string, string> = {
  summary: 'Summary', trl: 'TRL', value_levers: 'Value levers', researchers_ip: 'Researchers & IP',
  problem_applications: 'Problem & applications', university_details: 'University details',
  industry_insights: 'Industry insights', similar_tech: 'Similar technologies',
};
export const SECTION_HINTS: Record<string, string> = {
  summary: 'utech_summary — short = first two sentences, full = the whole summary',
  trl: 'TRL level, its label and where it came from',
  value_levers: 'Value lever chips (placeholder until the source is wired)',
  researchers_ip: 'Researcher names (never e-mails), IP summary, publications summary, patent numbers',
  problem_applications: 'Problem addressed and example applications (Horizon results)',
  university_details: 'University name, label and country (right column)',
  industry_insights: '"Discover N technologies from <industry>" (right column)',
  similar_tech: 'The three most similar technologies (by meaning; settings below)',
};
/** Sections whose text is the product: showing them pre-login makes them public and crawlable. */
export const SENSITIVE_SECTIONS = new Set(['summary', 'researchers_ip', 'problem_applications']);

export const MODES = ['visible', 'locked', 'hidden'];
export const SUMMARY_MODES = ['short', 'full', 'locked', 'hidden'];
export const MODE_LABELS: Record<string, string> = {
  visible: 'Visible', locked: 'Locked (blurred + sign-in / upgrade)', hidden: 'Hidden',
  short: 'Short (first 2 sentences)', full: 'Full',
};

export type Matrix = Record<string, Record<string, string>>;

/** The contract defaults — what "Reset to defaults" puts back. */
export const DEFAULT_SECTIONS: Matrix = {
  summary: { pre_login: 'locked', post_login: 'short', paid: 'full' },
  trl: { pre_login: 'visible', post_login: 'visible', paid: 'visible' },
  value_levers: { pre_login: 'visible', post_login: 'visible', paid: 'visible' },
  researchers_ip: { pre_login: 'locked', post_login: 'visible', paid: 'visible' },
  problem_applications: { pre_login: 'locked', post_login: 'locked', paid: 'visible' },
  university_details: { pre_login: 'locked', post_login: 'locked', paid: 'visible' },
  industry_insights: { pre_login: 'visible', post_login: 'visible', paid: 'visible' },
  similar_tech: { pre_login: 'visible', post_login: 'visible', paid: 'visible' },
};

export const CTAS = ['contact_university', 'ip_assessment', 'share', 'bookmark'] as const;
export const CTA_LABELS: Record<string, string> = {
  contact_university: 'Contact University', ip_assessment: 'Get IP Assessment', share: 'Share', bookmark: 'Bookmark',
};
export const DEFAULT_CTAS: Matrix = Object.fromEntries(CTAS.map(c =>
  [c, { pre_login: 'shown', post_login: 'shown', paid: 'shown' }]));

export const SHARE_LABELS: Record<string, string> = {
  native: 'Native share sheet', copy: 'Copy link', linkedin: 'LinkedIn', whatsapp: 'WhatsApp', x: 'X', email: 'E-mail',
};
export const SOURCES = ['organic', 'direct', 'referral', 'social', 'internal'];
export const SOURCE_LABELS: Record<string, string> = {
  organic: 'Organic search', direct: 'Direct', referral: 'Referral', social: 'Social', internal: 'Internal (from our site)',
};

/** The owner's sheet (LIVE_&_PIPELINE, column D), code → label. The server
 *  holds the same list for universities/fill_labels; this copy only lets the
 *  confirm dialog say what will be written before anything is. */
export const SHEET_LABELS: Record<string, string> = {
  'UTECH-MIT': 'MIT', 'UTECH-JHU': 'Johns Hopkins', 'UTECH-STF': 'Stanford', 'UTECH-PUR': 'Purdue',
  'UTECH-ARI': 'Arizona', 'UTECH-UCLA': 'UCLA', 'UTECH-WARF': 'Wisconsin', 'UTECH-CSIR': 'CSIR',
  'UTECH-ASU': 'Arizona State', 'UTECH-UPT': 'Pittsburgh', 'UTECH-COR': 'Cornell', 'UTECH-COL': 'Columbia',
  'UTECH-AGI': 'Agrinnovate', 'UTECH-UCSD': 'UC San Diego', 'UTECH-GAT': 'Georgia Tech', 'UTECH-UFI': 'Florida',
  'UTECH-MNS': 'Minnesota', 'UTECH-SUNY': 'SUNY', 'UTECH-IITM': 'IIT Madras', 'UTECH-UCI': 'UC Irvine',
  'UTECH-IITB': 'IIT Bombay', 'UTECH-UPEN': 'UPenn', 'UTECH-SRM': 'SRMIST', 'UTECH-IISC': 'IISc',
  'UTECH-IITI': 'IIT Indore', 'UTECH-UMV': 'Maryland', 'UTECH-OXU': 'Oxford', 'UTECH-MPI': 'Max Planck',
  'UTECH-UOT': 'Toronto', 'UTECH-VIT': 'VIT India', 'UTECH-EPFL': 'EPFL', 'UTECH-KIT': 'KIT',
  'UTECH-IITD': 'IIT Delhi', 'UTECH-NRDC': 'NRDC', 'UTECH-RWTH': 'RWTH Aachen', 'UTECH-MOU': 'Monash',
  'UTECH-NCU': 'Newcastle', 'UTECH-UOS': 'Sydney', 'UTECH-DHU': 'Durham', 'UTECH-UOM': 'Melbourne',
};

/** The corpus' twelve canonical major categories (homepage_analytics.json,
 *  largest first), offered in the industry filter; free text still works. */
export const KNOWN_INDUSTRIES = [
  'Healthcare & Life Sciences', 'Advanced Materials & Manufacturing', 'Electronics, IoT & Robotics',
  'ICT, AI & Computing', 'Energy & Sustainability', 'Agriculture & Food', 'Transportation & Mobility',
  'Construction & Smart Cities', 'Safety, Defense & First Responders', 'Science, Exploration & Space',
  'Consumer Goods & Services', 'Education, HR & Training',
];

// ---------------------------------------------------------------------------
// Event labels — from the public pages' own catalogue, so a new techpage_* /
// unipage_* action is labelled here the moment the page starts sending it.
// ---------------------------------------------------------------------------
const EVENTS: Record<string, any> = TECHPAGE_EVENTS as Record<string, any>;

export const eventLabel = (action: any): string => {
  const a = str(action);
  const e = EVENTS[a];
  if (typeof e === 'string') return e;
  if (e && typeof e.label === 'string' && e.label) return e.label;
  if (!a) return '—';
  return a.replace(/^(techpage|unipage|unis)_/, '').replace(/_/g, ' ');
};

/** Human name for a section / CTA / share channel / state / source id. */
export const nameOf = (id: any, ...maps: Record<string, string>[]): string => {
  const k = str(id);
  for (const m of maps) if (m[k]) return m[k];
  return k ? k.replace(/_/g, ' ') : '—';
};

export const propsText = (p: any): string => {
  if (!p) return '';
  let o = p;
  if (typeof p === 'string') { try { o = JSON.parse(p); } catch { return p; } }
  if (typeof o !== 'object') return String(o);
  return Object.entries(o)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ');
};

/** {label, value}[] out of [{<key>, n|count}] or {key: n}; sorted, largest first. */
export const breakdown = (v: any, labelOf: (k: string) => string = k => k,
                          keys: string[] = ['label', 'key', 'source', 'cta', 'state', 'section', 'channel', 'action', 'name']):
  { label: string; value: number }[] => {
  let out: { label: string; value: number }[] = [];
  if (Array.isArray(v)) {
    out = v.map(x => {
      const k = keys.map(kk => x?.[kk]).find(y => y !== undefined && y !== null && y !== '');
      return { label: labelOf(str(k)), value: num(x?.n ?? x?.count ?? x?.value ?? x?.views) };
    }).filter(x => x.label);
  } else if (v && typeof v === 'object') {
    out = Object.entries(v).map(([k, n]) => ({ label: labelOf(k), value: num(n) }));
  }
  return out.sort((a, b) => b.value - a.value);
};

/** Opens the public page on this site (the admin runs on the same origin). */
export const publicHref = (path: any): string => {
  const p = str(path);
  if (!p) return '';
  return /^https?:\/\//i.test(p) ? p : (p.startsWith('/') ? p : `/${p}`);
};

export const OpenLink: React.FC<{ path: any; label?: string; className?: string }> = ({ path, label, className }) => {
  const href = publicHref(path);
  if (!href) return <span className="text-grayscale-1100">—</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={`Open ${str(path)} in a new tab`}
      className={className || 'text-primary-700 underline underline-offset-2 whitespace-nowrap'}>
      {label ?? 'Open ↗'}
    </a>
  );
};

// ---------------------------------------------------------------------------
// Date range — shared by Overview and the two page tables (panel-level state).
// ---------------------------------------------------------------------------
export type Range = { preset: string; from: string; to: string; excludeInternal: boolean };

const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const presetRange = (days: number): { from: string; to: string } => {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return { from: ymdLocal(from), to: ymdLocal(to) };
};

export const defaultRange = (): Range => ({ preset: '30', ...presetRange(30), excludeInternal: true });

export const rangeParams = (r: Range): Record<string, string | number> =>
  ({ from: r.from, to: r.to, exclude_internal: r.excludeInternal ? 1 : 0 });

const PRESETS: Record<string, string> = {
  '1': 'Today', '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last 12 months', custom: 'Custom dates',
};

export const RangeBar: React.FC<{ value: Range; onChange: (r: Range) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
    <span className="text-grayscale-1100">Period</span>
    <Select value={value.preset} options={Object.keys(PRESETS)} labels={PRESETS}
      onChange={p => onChange(p === 'custom' ? { ...value, preset: p } : { ...value, preset: p, ...presetRange(Number(p)) })} />
    <span className="text-grayscale-1100">from</span>
    <TextInput type="date" value={value.from} onChange={v => onChange({ ...value, preset: 'custom', from: v })} />
    <span className="text-grayscale-1100">to</span>
    <TextInput type="date" value={value.to} onChange={v => onChange({ ...value, preset: 'custom', to: v })} />
    <label className="inline-flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
      title="Leave out the team's own visits, known bots and our internal / staging origins">
      <input type="checkbox" checked={value.excludeInternal}
        onChange={e => onChange({ ...value, excludeInternal: e.target.checked })} />
      Exclude team, bots &amp; internal
    </label>
  </div>
);

// ---------------------------------------------------------------------------
// Daily bars — one metric at a time; readout line instead of a floating tooltip.
// ---------------------------------------------------------------------------
export const DailyBars: React.FC<{
  days: any[]; series: { key: string; label: string }[]; height?: string; empty?: string;
}> = ({ days, series, height = 'h-40', empty }) => {
  const available = series.filter(s => days.some(d => d && d[s.key] !== undefined && d[s.key] !== null));
  const list = available.length ? available : series;
  const [metric, setMetric] = useState(list[0]?.key || '');
  const [hover, setHover] = useState<number | null>(null);
  const spec = list.find(s => s.key === metric) || list[0];
  const pts = days.map(d => ({ day: str(d?.day ?? d?.date ?? d?.d), value: num(d?.[spec?.key || '']) }));
  const max = Math.max(1, ...pts.map(p => p.value));
  const total = pts.reduce((a, p) => a + p.value, 0);
  const dayLabel = (s: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    const t = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
    return isNaN(t.getTime()) ? s : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };
  const shown = hover !== null ? pts[hover] : null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {list.length > 1 ? (
          <div className="flex gap-1 flex-wrap">
            {list.map(s => (
              <button key={s.key} type="button" onClick={() => setMetric(s.key)}
                className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${spec?.key === s.key
                  ? 'bg-primary-500 text-grayscale-100 font-bold'
                  : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                {s.label}
              </button>
            ))}
          </div>
        ) : <span className="text-xs text-grayscale-1200">{spec?.label}</span>}
        <div className="text-xs text-grayscale-1100 min-h-[1rem]" aria-live="polite">
          {shown ? <><span className="font-bold text-grayscale-1300">{shown.value.toLocaleString()}</span> {spec?.label.toLowerCase()} on {dayLabel(shown.day)}</>
            : <>Total <span className="font-bold text-grayscale-1300">{total.toLocaleString()}</span> over {pts.length} day{pts.length === 1 ? '' : 's'}</>}
        </div>
      </div>
      {pts.length === 0 ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">{empty || 'No daily data in this period.'}</div>
      ) : (
        <div className="flex gap-2">
          <div className={`flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-10 shrink-0 ${height}`}>
            <span>{max.toLocaleString()}</span><span>0</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`relative ${height} border-b border-grayscale-300`} onMouseLeave={() => setHover(null)}>
              <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-grayscale-200" />
              <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-grayscale-200" />
              <div className="absolute inset-0 flex items-end gap-[2px]">
                {pts.map((p, i) => (
                  <div key={p.day || i} className="flex-1 h-full flex items-end" onMouseEnter={() => setHover(i)}
                    title={`${dayLabel(p.day)}: ${p.value.toLocaleString()}`}>
                    <div className={`w-full rounded-t ${hover === i ? 'bg-primary-700' : 'bg-primary-500'}`}
                      style={{ height: p.value ? `max(2px, ${(p.value / max) * 100}%)` : '0' }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-grayscale-1100 mt-1">
              <span>{dayLabel(pts[0].day)}</span>
              {pts.length > 2 && <span>{dayLabel(pts[Math.floor(pts.length / 2)].day)}</span>}
              <span>{dayLabel(pts[pts.length - 1].day)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small controls
// ---------------------------------------------------------------------------
/** An on/off switch. `disabled` greys it out and the title explains why. */
export const Toggle: React.FC<{
  on: boolean; onChange: (v: boolean) => void; disabled?: boolean; busy?: boolean; title?: string; label?: string;
}> = ({ on, onChange, disabled, busy, title, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label || title}
    disabled={disabled || busy} title={title}
    onClick={e => { e.stopPropagation(); onChange(!on); }}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors
      ${on ? 'bg-primary-500' : 'bg-grayscale-600'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      ${busy ? 'opacity-60' : ''}`}>
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform
      ${on ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
  </button>
);

/** Click-to-edit single line (same look as the Universities sheet's editor). */
export const InlineEdit: React.FC<{
  value: string | null | undefined; placeholder?: string; title?: string;
  muted?: boolean; warn?: boolean; mono?: boolean; width?: string;
  onSave: (v: string) => Promise<string | void>;
}> = ({ value, placeholder = 'set…', title, muted, warn, mono, width = 'w-32', onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const commit = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    let e: string | void;
    try { e = await onSave(draft); } catch (x: any) { e = String(x?.message || x || 'Save failed'); }
    setBusy(false);
    if (e) { setErr(e); return; }
    setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
        <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape' && !busy) setEditing(false); }}
          className={`h-7 px-1.5 ${width} rounded border-[0.5px] border-primary-300 text-xs bg-white ${mono ? 'font-mono' : ''}`} />
        <Btn small disabled={busy} onClick={commit}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small disabled={busy} onClick={() => setEditing(false)}>Cancel</Btn>
        {err && <span className="text-[11px] text-red-600 basis-full">{err}</span>}
      </span>
    );
  }
  const has = !!(value && String(value).trim());
  return (
    <button type="button" onClick={() => { setEditing(true); setDraft(value || ''); setErr(''); }}
      title={title || (has ? 'Click to edit' : 'Not set — click to add')}
      className={`underline underline-offset-2 decoration-dotted text-left ${mono ? 'font-mono' : ''} ${
        has ? (muted ? 'text-grayscale-1100 italic' : 'text-grayscale-1300')
            : (warn ? 'text-amber-700' : 'text-grayscale-1100')}`}>
      {has ? value : placeholder}
    </button>
  );
};

/** Closes a drawer / dialog on Escape. */
export function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

// ---------------------------------------------------------------------------
// Excel exports (admin routes, audited server-side).
// ---------------------------------------------------------------------------
const uid = () => apiService.getStoredUserId() || '';
const ymd = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

export async function downloadXlsx(path: string, params: Record<string, string | number>, fallback: string): Promise<string> {
  const clean: Record<string, string | number> = {};
  Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) clean[k] = v; });
  const { blob, filename, error } = await apiService.adminFetchFile(uid(), path, clean);
  if (error || !blob) return error || 'Download failed';
  saveBlob(blob, filename || `${fallback}-${ymd()}.xlsx`);
  return '';
}

export const ExcelButton: React.FC<{
  path: string; params: Record<string, string | number>; fallback: string; what: string; onError: (e: string) => void;
}> = ({ path, params, fallback, what, onError }) => {
  const [busy, setBusy] = useState(false);
  return (
    <Btn small kind="primary" disabled={busy} title={`${what} (same filters) as an Excel workbook`}
      onClick={async () => {
        setBusy(true); onError('');
        const e = await downloadXlsx(path, params, fallback);
        setBusy(false);
        if (e) onError(e);
      }}>
      {busy ? 'Preparing…' : 'Download Excel'}
    </Btn>
  );
};

export const fmtSeconds = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Math.round(num(v));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60), s = n % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

export const fmtInt = (v: any): string =>
  v === null || v === undefined || v === '' ? '—' : num(v).toLocaleString();
